'use client';

import { useEffect, useState } from 'react';
import {
  IconArrowLeft,
  IconSend,
  IconPaperclip,
  IconUser,
  IconShield,
  IconClock
} from '@tabler/icons-react';
import {
  Card,
  Text,
  Title,
  Textarea,
  Button,
  Badge,
  Select,
  Avatar,
  Group,
  Stack,
  Divider,
  Grid,
  Box,
  ActionIcon,
  ScrollArea
} from '@mantine/core';
import { getHeader, getUserType } from '../../../config/userSession';
import { Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

export function TicketDetail({ ticket, onBack, handleGetSupportTickets }) {
  const [responseMessage, setResponseMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userType = getUserType();
  const [opened, { open, close }] = useDisclosure(false);
  const [pendingStatus, setPendingStatus] = useState(null);

  const [ticketResponse, setTicketResponses] = useState([]);
  const confirmStatusChange = (newStatus) => {
    setPendingStatus(newStatus);
    open();
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingStatus) return;

    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/account/update-support-ticket`,
      {
        method: 'PUT',
        headers: getHeader(),
        body: JSON.stringify({
          status: pendingStatus,
          conversationId: ticket.id
        })
      }
    );

    if (response.ok) {
      onBack();
      handleGetSupportTickets();
    }
    close();
  };

  const handleGetConversations = async (values) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/account/get-support-requests-messages/${ticket.id}`,
        {
          method: 'GET',
          headers: getHeader()
        }
      );

      const result = await response.json();
      setTicketResponses(result.response);
      if (!response.ok) {
        throw new Error(result.message || 'Failed to send email');
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  useEffect(() => {
    handleGetConversations();
  }, []);
  console.log(userType);
  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    if (!responseMessage.trim()) return;

    setIsSubmitting(true);

    // Simulate API call
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/account/send-support-requests-message`,
        {
          method: 'POST',
          headers: getHeader(),
          body: JSON.stringify({
            reply: responseMessage,
            conversationId: ticket.id
          })
        }
      );

      const result = await response.json();
      console.log(result);
      if (!response.ok) {
        throw new Error(result.message || 'Failed to send email');
      } else {
        handleGetConversations();
        setIsSubmitting(false);
        setResponseMessage('');
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'red';
      case 'in_progress':
        return 'blue';
      case 'resolved':
        return 'green';
      case 'closed':
        return 'gray';
      default:
        return 'blue';
    }
  };

  return (
    <Stack spacing="lg">
      {/* Header */}
      <Group position="apart">
        <Group>
          <ActionIcon onClick={onBack} size="lg">
            <IconArrowLeft size={20} />
          </ActionIcon>
          <div>
            <Title order={2}>{ticket.subject}</Title>
            <Text color="dimmed">Ticket #{ticket.id}</Text>
          </div>
        </Group>
        {userType === 'Admin' && (
          <Group>
            <Select
              value={ticket.status}
              onChange={confirmStatusChange}
              data={[
                { value: 'open', label: 'Open' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'resolved', label: 'Resolved' },
                { value: 'closed', label: 'Closed' }
              ]}
              style={{ width: 140 }}
            />
          </Group>
        )}
      </Group>

      <Grid>
        {/* Main Content */}
        <Grid.Col lg={8}>
          <Stack spacing="md">
            {/* Original Request */}
            <Card withBorder>
              <Group mb="md">
                <IconUser size={20} />
                <Title order={3}>Original Request</Title>
              </Group>

              <Group noWrap align="flex-start">
                <Avatar color="blue" radius="xl">
                  {ticket.email}
                </Avatar>

                <Stack spacing="xs" style={{ flex: 1 }}>
                  <Group spacing="xs">
                    <Text weight={500}>{ticket.email}</Text>
                    <Text size="sm" color="dimmed">
                      {new Date(ticket.createdAt).toLocaleDateString()} at{' '}
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </Text>
                  </Group>

                  <Card withBorder p="md">
                    <Text style={{ whiteSpace: 'pre-wrap' }}>
                      {ticket.description}
                    </Text>
                  </Card>

                  {/* {ticket.attachments.length > 0 && (
                    <Box mt="sm">
                      <Text size="sm" weight={500} mb="xs">
                        Attachments:
                      </Text>
                      <Group spacing="xs">
                        {ticket.attachments.map((attachment, index) => (
                          <Badge
                            key={index}
                            variant="outline"
                            leftSection={<IconPaperclip size={12} />}
                          >
                            {attachment}
                          </Badge>
                        ))}
                      </Group>
                    </Box>
                  )} */}
                </Stack>
              </Group>
            </Card>

            {/* Responses */}
            {ticketResponse.length > 0 && (
              <Card withBorder>
                <Title order={3} mb="md">
                  Conversation
                </Title>

                <ScrollArea
                  style={{ maxHeight: 400, minHeight: 50 }}
                  type="auto"
                >
                  <Stack spacing="md">
                    {ticketResponse.map((response) => (
                      <Group key={response.id} noWrap align="flex-start">
                        <Avatar
                          color={response.isAdmin ? 'blue' : 'gray'}
                          radius="xl"
                        >
                          {response.isAdmin ? (
                            <IconShield size={16} />
                          ) : (
                            <IconUser size={16} />
                          )}
                        </Avatar>

                        <Stack spacing="xs" style={{ flex: 1 }}>
                          <Group spacing="xs">
                            <Text weight={500}>{response.userId}</Text>
                            {response.isAdmin && (
                              <Badge size="sm" variant="light">
                                Admin
                              </Badge>
                            )}
                            <Text size="sm" color="dimmed">
                              {new Date(ticket.createdAt).toLocaleDateString()}{' '}
                              at{' '}
                              {new Date(ticket.createdAt).toLocaleTimeString()}
                            </Text>
                          </Group>

                          <Card
                            withBorder
                            p="md"
                            style={{
                              backgroundColor: response.isAdmin
                                ? '#f0f9ff'
                                : '#f8f9fa',
                              borderColor: response.isAdmin
                                ? '#b6e0fe'
                                : '#e9ecef'
                            }}
                          >
                            <Text style={{ whiteSpace: 'pre-wrap' }}>
                              {response.content}
                            </Text>
                          </Card>
                        </Stack>
                      </Group>
                    ))}
                  </Stack>
                </ScrollArea>
              </Card>
            )}

            {/* Response Form */}
            <Card withBorder>
              <Title order={3} mb="xs">
                Add Response
              </Title>
              <Text color="dimmed" mb="md">
                Reply to the customer's request
              </Text>

              <form onSubmit={handleSubmitResponse}>
                <Stack spacing="md">
                  <Textarea
                    placeholder="Type your response here..."
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.currentTarget.value)}
                    minRows={6}
                    required
                    disabled={ticket.status === 'closed'}
                  />

                  <Group position="right">
                    <Button
                      type="submit"
                      disabled={!responseMessage.trim() || isSubmitting}
                      leftIcon={<IconSend size={16} />}
                      loading={isSubmitting}
                    >
                      Send Response
                    </Button>
                  </Group>
                </Stack>
              </form>
            </Card>
          </Stack>
        </Grid.Col>

        {/* Sidebar */}
        <Grid.Col lg={4}>
          <Stack spacing="md">
            <Card withBorder>
              <Title order={3} mb="md">
                Ticket Information
              </Title>

              <Stack spacing="sm">
                {userType === 'Admin' && (
                  <div>
                    <Text size="sm" weight={500} color="dimmed">
                      Customer
                    </Text>
                    <Text size="sm">{ticket.userId}</Text>
                  </div>
                )}

                <div>
                  <Text size="sm" weight={500} color="dimmed">
                    Category
                  </Text>
                  <Text size="sm" transform="capitalize">
                    {ticket.issueType}
                  </Text>
                </div>

                <div>
                  <Text size="sm" weight={500} color="dimmed">
                    Status
                  </Text>
                  <Badge color={getStatusColor(ticket.status)} variant="light">
                    {ticket.status.replace('-', ' ').toUpperCase()}
                  </Badge>
                </div>

                <div>
                  <Text size="sm" weight={500} color="dimmed">
                    Created
                  </Text>
                  <Group spacing="xs">
                    <IconClock size={14} />
                    <Text size="sm">
                      {new Date(ticket.createdAt).toLocaleDateString()} at{' '}
                      {new Date(ticket.createdAt).toLocaleTimeString()}
                    </Text>
                  </Group>
                </div>

                <div>
                  <Text size="sm" weight={500} color="dimmed">
                    Last Updated
                  </Text>
                  <Group spacing="xs">
                    <IconClock size={14} />
                    <Text size="sm">
                      {new Date(ticket.createdAt).toLocaleDateString()} at{' '}
                      {new Date(ticket.createdAt).toLocaleTimeString()}
                    </Text>
                  </Group>
                </div>
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>
      <Modal
        opened={opened}
        onClose={close}
        title="Confirm Status Change"
        centered
      >
        <Text>
          Are you sure you want to change the ticket status to {pendingStatus}?
        </Text>
        <Group mt="md" position="right">
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button color="blue" onClick={handleConfirmStatusChange}>
            Confirm
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
}
